<!--
 * @Author: Nemo
 * @Description: 响应式弹窗组件 - PC端实现
 * @Date: 2024-05-29
-->
<template>
  <div v-if="visible || closing" class="responsive-dialog">
    <!-- 弹窗蒙层 -->
    <div class="responsive-dialog-mask" @click="handleMaskClick" :class="{ 'mask-fade-out': closing }" />

    <!-- PC端对话框 -->
    <div class="responsive-dialog-container pc-dialog" :class="{ 'fade-out': closing }">
      <div class="dialog-header">
        <div class="dialog-title-wrapper">
          <img v-if="type" :src="typeIcon" class="payment-icon"
            :class="{ 'apple-pay-icon': type === 'apple', 'google-pay-icon': type === 'google' }" alt="" />
          <div class="dialog-title">{{ displayTitle }}</div>
        </div>
        <div class="dialog-close" @click="handleClose">
          <i class="el-icon-close"></i>
        </div>
      </div>
      <div class="dialog-content">
        <billing-form v-if="showBillingForm" ref="billingForm" :fields="actualBillingFields" :order-info="orderInfo"
          :payment-type="type" @field-focus="handleFieldFocus" @field-change="handleFieldChange" />
        <slot></slot>
      </div>
      <div class="dialog-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
      <div class="dialog-footer" v-else-if="showDefaultFooter">
        <button class="dialog-confirm-btn" @click="handleConfirm">
          <img v-if="type === 'google'" src="./image/icon_google.png" alt="Google Pay" class="btn-icon" />
          <img v-if="type === 'apple'" src="./image/icon_apple.png" alt="Apple Pay" class="btn-icon" />
          {{ $t('message.checkout.pay') }}
          <template v-if="orderInfo && orderInfo.txnAmount && orderInfo.txnCurrency">
            {{ formatMoneyWithCommas(orderInfo.txnAmount) }} {{ orderInfo.txnCurrency }}
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatMoneyWithCommas } from '@/utils/tools/format';

import BillingForm from './billing-form.vue';
import ApplePayIcon from './image/icon_apple_pay.svg';
import GooglePayIcon from './image/icon_google_pay.png';
export default {
  name: 'responsive-dialog-pc',
  components: {
    BillingForm,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
      validator: function (value) {
        return ['', 'apple', 'google'].includes(value);
      },
    },
    closeOnClickMask: {
      type: Boolean,
      default: true,
    },
    showDefaultFooter: {
      type: Boolean,
      default: true,
    },
    confirmText: {
      type: String,
      default: '确认',
    },
    cancelText: {
      type: String,
      default: '取消',
    },
    billingFields: {
      type: Array,
      default: () => [],
    },
    showBillingForm: {
      type: Boolean,
      default: false,
    },
    orderInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    displayTitle() {
      if (this.type === 'apple') {
        return 'Apple Pay';
      } else if (this.type === 'google') {
        return 'Google Pay';
      }
      return this.title;
    },
    typeIcon() {
      if (this.type === 'apple') {
        return ApplePayIcon;
      } else if (this.type === 'google') {
        return GooglePayIcon;
      }
      return '';
    },
    actualBillingFields() {
      return this.billingFields;
    },
  },
  data() {
    return {
      closing: false,
    };
  },
  mounted() {
    // 弹窗出现时禁止背景滚动
    if (this.visible) {
      document.body.style.overflow = 'hidden';
    }
  },
  watch: {
    visible(val) {
      // 当弹窗显示或隐藏时，控制背景滚动
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    formatMoneyWithCommas,
    handleClose() {
      // 开始关闭动画
      this.closing = true;

      // 动画结束后再真正关闭
      setTimeout(() => {
        this.closing = false;
        this.$emit('update:visible', false);
        this.$emit('close');
        document.body.style.overflow = '';
      }, 300); // 动画时长，与CSS中的duration一致
    },
    handleMaskClick() {
      if (this.closeOnClickMask) {
        this.handleClose();
      }
    },
    handleConfirm() {
      if (this.showBillingForm && this.$refs.billingForm) {
        // 使用billing-form组件的validate方法验证表单
        this.$refs.billingForm.validate()
          .then(formData => {
            // 验证通过，发送成功事件
            this.$emit('confirm', formData);
          })
          .catch(error => {
            // 验证失败不触发confirm事件
            console.log('error-form', error);
            this.$nextTick(() => {
              const errorDom = document.querySelector('.is-error');
              if (errorDom) {
                errorDom.scrollIntoView({
                  block: 'center',
                  behavior: 'smooth',
                });
              }
            });
          });
      } else {
        this.$emit('confirm');
      }
    },
    handleFieldFocus(fieldName) {
      this.$emit('field-focus', fieldName);
    },
    handleFieldChange(fieldData) {
      this.$emit('field-change', fieldData);
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== 共用样式 ===== */
.responsive-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  backdrop-filter: blur(4px);
  transition: opacity 0.3s ease;

  &.mask-fade-out {
    opacity: 0;
  }
}

.responsive-dialog-container {
  position: fixed;
  z-index: 2001;
  background: #FFFFFF;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* ===== PC端样式 ===== */
.pc-dialog {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 852px;
  height: 679px;
  border-radius: 18px;

  &.fade-out {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
}

.pc-dialog .dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 24px 24px;
}

.pc-dialog .dialog-title-wrapper {
  display: flex;
  align-items: center;
}

.pc-dialog .payment-icon {
  height: 32px;
  width: auto;
  margin-right: 16px;
}

.pc-dialog .dialog-title {
  font-weight: 600;
  font-size: 18px;
  color: #1A202C;
}

.pc-dialog .dialog-close {
  cursor: pointer;
  color: #8D98AF;
}

.pc-dialog .dialog-close:hover {
  color: #182058;
}

.pc-dialog .dialog-content {
  padding: 24px 24px 0px 24px;
  max-height: 480px;
  overflow-y: auto;
  border: 1px solid #E5E8ED;
  margin: 0 24px 24px 24px;
  border-radius: 8px;
}

.pc-dialog .dialog-footer {
  position: absolute;
  bottom: 24px;
  right: 24px;
  left: 24px;
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  background: #FFFFFF;
  border-top: 1px solid #E5E8ED;
}

.pc-dialog .dialog-footer button {
  padding: 8px 52px;
  margin-left: 10px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.pc-dialog .dialog-cancel-btn {
  background: #F0F3F6;
  color: #182058;
}

.pc-dialog .dialog-cancel-btn:hover {
  background: #E5E8ED;
}

.pc-dialog .dialog-confirm-btn {
  background: #000000;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pc-dialog .dialog-confirm-btn:hover {
  background: #1A1A1A;
}

/* ===== 特定图标样式 ===== */
.apple-pay-icon {
  width: 50px !important;
}

.google-pay-icon {
  width: 60.16px !important;
}

.btn-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

:deep(.el-form-item__error) {
  text-align: left !important;
}
</style>
